import { useEffect, useState } from "react";
import { Navigate, Outlet, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Inbox, Boxes, Package, LogOut, ExternalLink, Menu, X, FileText, Images, MessageSquareQuote, Layers, Ticket } from "lucide-react";
import { getToken, getAdminEmail, clearSession, fetchMe } from "@/lib/adminApi";
import ThemeToggle from "@/components/site/ThemeToggle";
import PWAInstallButton from "@/components/admin/PWAInstallButton";

const SIDEBAR_LINKS = [
    { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
    { to: "/admin/projects", label: "Deliveries", icon: Boxes },
    { to: "/admin/content", label: "Content", icon: FileText },
    { to: "/admin/portfolio", label: "Portfolio", icon: Images },
    { to: "/admin/hero-rail", label: "Hero Rail", icon: Layers },
    { to: "/admin/reviews", label: "Reviews", icon: MessageSquareQuote },
    { to: "/admin/packages", label: "Packages", icon: Package },
    { to: "/admin/coupons", label: "Coupons", icon: Ticket },
];

export default function AdminLayout() {
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        if (!getToken()) return;
        fetchMe().catch(() => {
            clearSession();
            navigate("/admin/login", { replace: true });
        });
    }, [navigate]);

    if (!getToken()) return <Navigate to="/admin/login" replace />;

    const email = getAdminEmail();
    const onLogout = () => {
        clearSession();
        navigate("/admin/login", { replace: true });
    };

    const SidebarBody = ({ onNav }) => (
        <div className="flex h-full flex-col">
            <div className="flex items-center justify-between gap-2 px-5 py-5 border-b" style={{ borderColor: "var(--pg-border)" }}>
                <div className="flex items-center gap-2">
                    <svg width="24" height="24" viewBox="0 0 28 28" fill="none" aria-hidden>
                        <rect x="1.5" y="1.5" width="25" height="25" rx="7" stroke="var(--pg-fg)" strokeOpacity="0.4" />
                        <path d="M9 19V9h5.4c2 0 3.3 1.2 3.3 3.1S16.4 15.2 14.4 15.2H11" stroke="var(--pg-fg)" strokeWidth="1.6" strokeLinecap="round" />
                        <circle cx="21" cy="21" r="1.5" fill="var(--pg-accent)" />
                    </svg>
                    <span className="font-display text-base" style={{ color: "var(--pg-fg)" }}>
                        Pranto<span style={{ color: "var(--pg-accent)" }}>.</span>Gfx
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <PWAInstallButton variant="chip" />
                    <ThemeToggle />
                </div>
            </div>

            <span
                className="px-5 pb-2 pt-5 font-mono text-[10px] uppercase tracking-[0.22em]"
                style={{ color: "var(--pg-fg-muted)" }}
            >
                Studio CMS
            </span>

            <nav className="flex-1 px-3" aria-label="Admin">
                {SIDEBAR_LINKS.map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        onClick={onNav}
                        data-testid={`admin-nav-${l.to.split("/").pop()}`}
                        className={({ isActive }) =>
                            `pg-app-tap flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200 ${
                                isActive ? "bg-[var(--pg-surface-2)] text-[var(--pg-fg)]" : "text-[var(--pg-fg-muted)] hover:bg-[var(--pg-surface)] hover:text-[var(--pg-fg)]"
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg transition-all duration-200"
                                    style={{
                                        background: isActive ? "color-mix(in srgb, var(--pg-accent) 18%, transparent)" : "transparent",
                                        color: isActive ? "var(--pg-accent)" : "inherit",
                                    }}
                                >
                                    <l.icon className="h-4 w-4" strokeWidth={1.6} />
                                </span>
                                {l.label}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="border-t px-3 py-4 space-y-1" style={{ borderColor: "var(--pg-border)" }}>
                <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pg-app-tap flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-[var(--pg-surface)]"
                    style={{ color: "var(--pg-fg-muted)" }}
                >
                    <ExternalLink className="h-4 w-4" strokeWidth={1.6} />
                    View Live Site
                </a>
                <div className="px-3 pb-2 pt-3 text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                    Logged in as<br />
                    <span className="text-[var(--pg-fg)] text-xs">{email || "admin"}</span>
                </div>
                <button
                    type="button"
                    onClick={onLogout}
                    data-testid="admin-logout-button"
                    className="pg-app-tap flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-[var(--pg-surface)]"
                    style={{ color: "var(--pg-fg-muted)" }}
                >
                    <LogOut className="h-4 w-4" strokeWidth={1.6} />
                    Sign out
                </button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen" style={{ background: "var(--pg-bg-alt)" }}>
            <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[260px_1fr]">
                {/* Desktop Sidebar */}
                <aside
                    className="hidden lg:block border-r"
                    style={{ borderColor: "var(--pg-border)", background: "var(--pg-bg)" }}
                >
                    <SidebarBody />
                </aside>

                {/* Mobile top bar */}
                <div
                    className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b px-4 py-3 lg:hidden"
                    style={{ background: "var(--pg-bg)", borderColor: "var(--pg-border)" }}
                >
                    <div className="flex items-center gap-2">
                        <svg width="22" height="22" viewBox="0 0 28 28" fill="none" aria-hidden>
                            <rect x="1.5" y="1.5" width="25" height="25" rx="7" stroke="var(--pg-fg)" strokeOpacity="0.4" />
                            <path d="M9 19V9h5.4c2 0 3.3 1.2 3.3 3.1S16.4 15.2 14.4 15.2H11" stroke="var(--pg-fg)" strokeWidth="1.6" strokeLinecap="round" />
                            <circle cx="21" cy="21" r="1.5" fill="var(--pg-accent)" />
                        </svg>
                        <span className="font-display text-sm" style={{ color: "var(--pg-fg)" }}>
                            Studio CMS
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <PWAInstallButton variant="chip" />
                        <ThemeToggle />
                        <button
                            type="button"
                            onClick={() => setMobileOpen(true)}
                            data-testid="admin-mobile-menu"
                            aria-label="Open menu"
                            className="pg-app-tap inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                            style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg)" }}
                        >
                            <Menu className="h-4 w-4" strokeWidth={1.6} />
                        </button>
                    </div>
                </div>

                {/* Mobile drawer */}
                <AnimatePresence>
                    {mobileOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm lg:hidden"
                            onClick={() => setMobileOpen(false)}
                            data-testid="admin-mobile-drawer"
                        >
                            <motion.aside
                                onClick={(e) => e.stopPropagation()}
                                initial={{ x: "-100%" }}
                                animate={{ x: 0 }}
                                exit={{ x: "-100%" }}
                                transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                                className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] border-r"
                                style={{ background: "var(--pg-bg)", borderColor: "var(--pg-border)" }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setMobileOpen(false)}
                                    aria-label="Close menu"
                                    className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full"
                                    style={{ color: "var(--pg-fg-muted)" }}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                                <SidebarBody onNav={() => setMobileOpen(false)} />
                            </motion.aside>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Content */}
                <main className="min-h-screen">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
